import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  inject,
  OnDestroy,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, switchMap } from 'rxjs/operators';
import { CommandPaletteService, DYNAMIC_GROUP_LABELS } from '../../search/command-palette.service';
import { MatchRange, SearchGroup, SearchItem, SearchPreviewAction } from '../../search/command-palette.model';
import { SEARCH_SCOPES, searchKind } from '../../search/command-palette.preview';

interface ScopeChip {
  id: string;
  label: string;
  count: number;
  active: boolean;
}

// Command palette (busca universal, ⌘K). Agrega páginas, ações, visões e entidades numa única
// busca por relevância, 100% teclado (↑↓ navega, ↵ abre, Ctrl+D favorita, esc fecha). O conteúdo
// vem do CommandPaletteService — este componente cuida só de UI, teclado e navegação.
@Component({
  selector: 'app-navbar-search',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './navbar-search.component.html',
  styleUrl: './navbar-search.component.scss',
})
export class NavbarSearchComponent implements OnDestroy {
  @ViewChild('searchModal') private searchModal!: TemplateRef<unknown>;
  @ViewChild('searchInput') private searchInput?: ElementRef<HTMLInputElement>;

  private readonly modalService = inject(NgbModal);
  private readonly palette = inject(CommandPaletteService);
  private readonly router = inject(Router);
  private readonly cdr = inject(ChangeDetectorRef);
  private modalRef?: NgbModalRef;

  private readonly queryChange = new Subject<string>();
  private productSub?: Subscription;
  private favoritesSub?: Subscription;

  query = '';
  // Todos os grupos da busca; `visibleGroups` é o recorte do filtro em pílula (scope).
  groups: SearchGroup[] = [];
  visibleGroups: SearchGroup[] = [];
  scopes: ScopeChip[] = [];
  scope = 'all';
  flat: SearchItem[] = [];
  selected = 0;
  // Ação da prévia que acabou de copiar ("Copiado" por um instante).
  copied: string | null = null;
  private copiedTimer?: ReturnType<typeof setTimeout>;

  constructor() {
    // Produtos entram de forma assíncrona (catálogo grande, cacheado). Debounce evita filtrar a
    // cada tecla; switchMap descarta respostas de queries antigas.
    this.productSub = this.queryChange
      .pipe(
        debounceTime(120),
        switchMap((q) => this.palette.searchDynamic(q)),
      )
      .subscribe((groups) => {
        this.mergeDynamicGroups(groups);
        this.cdr.detectChanges();
      });

    // Favoritos chegam do servidor depois de abrir (e mudam pela estrela): o estado vazio mostra a
    // seção deles, então ele é refeito; com busca digitada só a estrela da linha muda.
    this.favoritesSub = this.palette.favoritesChanged.subscribe(() => {
      if (!this.modalRef) return;
      if (!this.query.trim()) this.recompute();
      this.cdr.detectChanges();
    });
  }

  ngOnDestroy(): void {
    this.productSub?.unsubscribe();
    this.favoritesSub?.unsubscribe();
    clearTimeout(this.copiedTimer);
  }

  open(): void {
    this.query = '';
    this.scope = 'all';
    this.palette.refreshUserViews();
    this.palette.loadFavorites();
    this.recompute();
    this.modalRef = this.modalService.open(this.searchModal, {
      centered: false,
      size: 'lg',
      scrollable: false,
      windowClass: 'cmdk-modal',
    });
    // Zera a ref em qualquer fechamento (esc, backdrop, seleção) — o toggle Ctrl+K depende disso.
    const clear = () => {
      this.modalRef = undefined;
    };
    this.modalRef.result.then(clear, clear);
    setTimeout(() => this.searchInput?.nativeElement.focus(), 30);
  }

  close(): void {
    this.modalRef?.dismiss();
  }

  onQueryChange(): void {
    this.recompute();
    this.queryChange.next(this.query);
  }

  // Reconstrói páginas/ações/recentes na hora; produtos chegam depois (mergeProductGroup).
  private recompute(): void {
    this.groups = this.palette.buildStatic(this.query);
    this.rebuildFlat();
  }

  // Substitui os grupos de ENTIDADES (produtos/anúncios/notas) pela resposta assíncrona mais
  // recente, mantendo páginas/ações, e reordena tudo por relevância.
  private mergeDynamicGroups(dynamic: SearchGroup[]): void {
    const staticGroups = this.groups.filter((g) => !DYNAMIC_GROUP_LABELS.includes(g.label));
    const merged = this.query.trim() ? [...staticGroups, ...dynamic] : staticGroups;
    this.groups = merged.sort((a, b) => b.rank - a.rank);
    this.rebuildFlat();
  }

  // Recorte do filtro + contagem de cada pílula (sobre TODOS os resultados, não só os do filtro).
  private rebuildFlat(): void {
    const searching = !!this.query.trim();
    const kindsOf = (id: string) => SEARCH_SCOPES.find((s) => s.id === id)?.kinds ?? [];
    const inScope = (item: SearchItem, id: string) => {
      const kinds = kindsOf(id);
      return kinds.length === 0 || kinds.includes(searchKind(item));
    };
    const scope = searching ? this.scope : 'all';
    this.visibleGroups = this.groups
      .map((g) => ({ ...g, items: g.items.filter((it) => inScope(it, scope)) }))
      .filter((g) => g.items.length > 0);
    const all = this.groups.flatMap((g) => g.items);
    this.scopes = searching
      ? SEARCH_SCOPES.map((s) => ({
          id: s.id,
          label: s.label,
          count: all.filter((it) => inScope(it, s.id)).length,
          active: s.id === this.scope,
        }))
      : [];
    this.flat = this.visibleGroups.flatMap((g) => g.items);
    if (this.selected >= this.flat.length) this.selected = Math.max(0, this.flat.length - 1);
  }

  setScope(id: string): void {
    this.scope = id;
    this.selected = 0;
    this.rebuildFlat();
    this.searchInput?.nativeElement.focus();
  }

  get searching(): boolean {
    return !!this.query.trim();
  }

  get noResults(): boolean {
    return this.searching && this.flat.length === 0;
  }

  /** Item realçado: a prévia mostra ele (só os que trazem prévia, ou seja, entidades). */
  get current(): SearchItem | undefined {
    return this.flat[this.selected];
  }

  get hasPreview(): boolean {
    return this.searching && !!this.current?.preview;
  }

  runAction(item: SearchItem, action: SearchPreviewAction): void {
    if (action.copy) {
      void navigator.clipboard?.writeText(action.copy).catch((): void => undefined);
      this.copied = action.label;
      clearTimeout(this.copiedTimer);
      this.copiedTimer = setTimeout(() => {
        this.copied = null;
        this.cdr.detectChanges();
      }, 1500);
      this.searchInput?.nativeElement.focus();
      return;
    }
    if (action.route) {
      this.palette.recordUse(item);
      this.close();
      this.router.navigateByUrl(action.route);
    }
  }

  isSelected(item: SearchItem): boolean {
    return this.flat[this.selected]?.id === item.id;
  }

  hover(item: SearchItem): void {
    const i = this.flat.findIndex((f) => f.id === item.id);
    if (i >= 0) this.selected = i;
  }

  select(item: SearchItem): void {
    this.palette.recordUse(item);
    this.palette.prepareLaunch(item);
    this.close();
    if (item.route) this.router.navigateByUrl(item.route);
    else item.run?.();
  }

  isFavorite(item: SearchItem): boolean {
    return this.palette.isFavorite(item.id);
  }

  // A estrela é irmã da linha (botão dentro de botão é HTML inválido): o clique nela não abre o item.
  toggleFavorite(item: SearchItem, event?: Event): void {
    event?.stopPropagation();
    this.palette.toggleFavorite(item);
    // O foco volta pra busca: as setas e o Enter continuam valendo.
    this.searchInput?.nativeElement.focus();
  }

  favoriteLabel(item: SearchItem): string {
    return `Favorito: ${item.title}`;
  }

  // Destaque do trecho casado: 3 pedaços (antes / match / depois) — sem innerHTML, escape nativo.
  parts(text: string, range?: MatchRange | null): { pre: string; mark: string; post: string } {
    if (!range) return { pre: text, mark: '', post: '' };
    return {
      pre: text.slice(0, range.start),
      mark: text.slice(range.start, range.start + range.len),
      post: text.slice(range.start + range.len),
    };
  }

  // Cor do ícone pelo que o item É (pedido, produto, anúncio, nota); páginas pelo tipo de sempre.
  iconTone(item: SearchItem): string {
    switch (searchKind(item)) {
      case 'order': return 'ic-order';
      case 'product': return 'ic-product';
      case 'ad': return 'ic-ad';
      case 'invoice': return 'ic-invoice';
      case 'config': return 'ic-config';
      default:
        if (item.type === 'action') return 'ic-action';
        if (item.type === 'view') return 'ic-view';
        return 'ic-page';
    }
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Tab' && this.searching) {
      // Tab troca o filtro em pílula (Shift+Tab volta); o foco fica na busca.
      event.preventDefault();
      const i = SEARCH_SCOPES.findIndex((s) => s.id === this.scope);
      const step = event.shiftKey ? -1 : 1;
      this.setScope(SEARCH_SCOPES[(i + step + SEARCH_SCOPES.length) % SEARCH_SCOPES.length].id);
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.selected = Math.min(this.selected + 1, this.flat.length - 1);
      this.scrollToSelected();
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.selected = Math.max(this.selected - 1, 0);
      this.scrollToSelected();
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const item = this.flat[this.selected];
      if (item) this.select(item);
    } else if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'd') {
      // Ctrl+D é o "favoritar" do navegador: aqui favorita o item realçado.
      event.preventDefault();
      const item = this.flat[this.selected];
      if (item?.route) this.toggleFavorite(item);
    }
  }

  private scrollToSelected(): void {
    setTimeout(() => document.querySelector('.cmdk-item.active')?.scrollIntoView({ block: 'nearest' }));
  }

  @HostListener('document:keydown', ['$event'])
  onGlobalKey(event: KeyboardEvent): void {
    if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
      event.preventDefault();
      if (this.modalRef) this.close();
      else this.open();
    }
  }
}
